import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Space } from 'antd';
import {
    HomeOutlined,
    UserOutlined,
    ClockCircleOutlined,
    CalendarOutlined,
    FileTextOutlined,
    QuestionCircleOutlined,
    DollarOutlined,
    GiftOutlined,
    FieldTimeOutlined,
    LogoutOutlined,
    SettingOutlined,
    DownOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    TeamOutlined,
    FileProtectOutlined,
    CheckSquareOutlined,
    DashboardOutlined,
    FileWordOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantTheme } from '../../../contexts/TenantThemeContext';
import TokenExpirationChecker from '../../Common/TokenExpirationChecker';
import logoHorizontal from '../../../assets/images/logo-horizontal.svg';
import ChangePasswordModal from '../../Header/ChangePasswordModal';
import styles from './EmployeeLayout.module.scss';

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

function resolveLogoUrl(logoUrl?: string, logoFilePath?: string): string | null {
    const src = logoUrl || logoFilePath;
    if (!src || typeof src !== 'string' || !src.trim()) return null;
    const trimmed = src.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    const path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
    const relPath = path.startsWith('uploads/') ? path.replace(/^uploads\//, '') : path;
    return `${API_BASE_URL}/api/uploads/${relPath}`;
}

const EMPLOYEE_ROLES = ['EMPLOYEE', 'CONTRACT_EMPLOYEE', 'INTERN'];

interface EmployeeLayoutProps {
    children: React.ReactNode;
}

const EmployeeLayout: React.FC<EmployeeLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { tenantConfig } = useTenantTheme() ?? {};
    const [collapsed, setCollapsed] = useState(false);
    const [changePasswordVisible, setChangePasswordVisible] = useState(false);

    const logoUrl = resolveLogoUrl(tenantConfig?.logo_url, tenantConfig?.logo_file_path);
    const logoSrc = logoUrl || logoHorizontal;

    const normalizedRoles = (user?.roles || []).map((r: string) =>
        r.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
    );
    const isEmployeeOnly = normalizedRoles.length > 0 && normalizedRoles.every((r: string) => EMPLOYEE_ROLES.includes(r));
    const hasAdminRole = !isEmployeeOnly;

    const upperRoles = (user?.roles || []).map((r: string) => r.toUpperCase());
    const hasManagerRole = upperRoles.some((r: string) => 
        r.includes('MANAGER') || 
        r.includes('ADMIN') || 
        r.includes('SUPERVISOR') || 
        r.includes('LEAD') || 
        r.includes('HR') || 
        r === 'DIRECTOR' || 
        r === 'EXECUTIVE'
    );
    const hasApprovePerm = (user?.permissions || []).some((p: string) => {
        const upper = p.toUpperCase();
        return upper.includes('_APPROVE') || upper.endsWith('APPROVE') || upper === 'APPROVE';
    });

    const canApprove = hasManagerRole || hasApprovePerm;

    const sidebarMenuItems = [
        { key: '/employee', label: 'Home', icon: <HomeOutlined /> },
        ...(canApprove ? [{ 
            key: '/employee/approvals', 
            label: 'Team Approvals', 
            icon: <CheckSquareOutlined style={{ color: '#52c41a' }} /> 
        }] : []),
        { key: '/employee/profile', label: 'My Profile', icon: <UserOutlined /> },
        { key: '/employee/attendance', label: 'Attendance', icon: <ClockCircleOutlined /> },
        { key: '/employee/timesheet', label: 'Timesheet', icon: <FieldTimeOutlined /> },
        { key: '/employee/leaves', label: 'Leaves', icon: <CalendarOutlined /> },
        { key: '/employee/holidays', label: 'Holidays', icon: <GiftOutlined /> },
        { key: '/employee/payslips', label: 'Payslips', icon: <FileTextOutlined /> },
        { key: '/employee/reimbursements', label: 'Reimbursements', icon: <DollarOutlined /> },
        { key: '/employee/policies', label: 'Company Policies', icon: <FileProtectOutlined /> },
        { key: '/employee/documents', label: 'Letters & Documents', icon: <FileWordOutlined /> },
        { key: '/employee/directory', label: 'Directory', icon: <TeamOutlined /> },
        { key: '/employee/helpdesk', label: 'Helpdesk', icon: <QuestionCircleOutlined /> },
    ];

    const selectedKey = sidebarMenuItems.find(item =>
        location.pathname === item.key ||
        (item.key !== '/employee' && location.pathname.startsWith(item.key))
    )?.key || '/employee';

    const userMenuItems = [
        { key: 'profile', label: 'My Profile', icon: <UserOutlined />, onClick: () => navigate('/employee/profile') },
        ...(hasAdminRole ? [{ key: 'admin-dash', label: 'Admin Dashboard', icon: <SettingOutlined />, onClick: () => navigate('/dashboard') }] : []),
        { key: 'change-password', label: 'Change Password', icon: <SettingOutlined />, onClick: () => setChangePasswordVisible(true) },
        { type: 'divider' as const },
        { key: 'logout', label: 'Logout', icon: <LogoutOutlined />, onClick: logout, danger: true },
    ];

    const displayName = user?.first_name && user?.last_name 
        ? `${user.first_name} ${user.last_name}` 
        : (user?.first_name || user?.username || user?.user_name || 'Employee');

    const handleToggle = () => setCollapsed(!collapsed);

    return (
        <Layout className={styles.employeeLayout}>
            <TokenExpirationChecker />

            {/* Sidebar */}
            <Sider
                collapsible
                collapsed={collapsed}
                trigger={null}
                collapsedWidth={64}
                width={240}
                className={styles.sidebar}
                theme="dark"
            >
                <div className={styles.sidebarInner}>
                    {/* Logo */}
                    <div className={styles.logo} onClick={() => navigate('/employee')}>
                        <img src={logoSrc} alt={tenantConfig?.company_name || 'VetanaHR'} className={styles.logoImg} />
                    </div>

                    {/* Menu */}
                    <Menu
                        mode="inline"
                        theme="dark"
                        selectedKeys={[selectedKey]}
                        className={styles.sidebarMenu}
                        onClick={({ key }) => navigate(key)}
                        items={sidebarMenuItems}
                    />
                </div>
            </Sider>

            {/* Main Area */}
            <Layout className={`${styles.mainContent} ${collapsed ? styles.collapsed : ''}`}>
                {/* Header */}
                <Header className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button className={styles.toggleBtn} onClick={handleToggle}>
                            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        </button>
                        {canApprove && (
                            <button
                                onClick={() => navigate('/dashboard')}
                                style={{
                                    marginLeft: 16,
                                    padding: '6px 12px',
                                    borderRadius: 6,
                                    border: '1px solid #1890ff',
                                    background: 'rgba(24, 144, 255, 0.1)',
                                    color: '#1890ff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <DashboardOutlined /> Admin Dashboard
                            </button>
                        )}
                    </div>

                    <div className={styles.headerRight}>
                        <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
                            <Space className={styles.userMenu}>
                                <Avatar
                                    size={34}
                                    src={(user as any)?.profile_picture || user?.profile_photo || user?.profilePhoto || user?.avatar}
                                    icon={!((user as any)?.profile_picture || user?.profile_photo || user?.profilePhoto || user?.avatar) && <UserOutlined />}
                                    className={styles.avatar}
                                />
                                <Text strong className={styles.userName}>{displayName}</Text>
                                <DownOutlined className={styles.dropdownArrow} />
                            </Space>
                        </Dropdown>
                    </div>
                </Header>

                {/* Content */}
                <Content className={styles.content}>
                    {children}
                </Content>

                {/* Footer */}
                <Footer className={styles.footer}>
                    <Text className={styles.footerText}>
                        © {new Date().getFullYear()} {tenantConfig?.company_name || 'VetanaHR'} — Powered by Vetana Software Solutions LLP
                    </Text>
                </Footer>
            </Layout>

            <ChangePasswordModal
                visible={changePasswordVisible}
                onClose={() => setChangePasswordVisible(false)}
            />
        </Layout>
    );
};

export default EmployeeLayout;
